import showdown from 'showdown';

const converter = new showdown.Converter({
  // Converts single newlines within paragraphs to <br />
  // (Showdown otherwise tends to ignore them)
  simpleLineBreaks: true,
  // Keep fenced code blocks intact (``` ... ```), so we don't accidentally
  // treat code newlines like normal text line breaks.
  ghCodeBlocks: true,
});

const EMPTY_PARAGRAPH_HTML = '<p></p>';

function trimConsecutiveNewLines(
  markdown: string,
  maxConsecutive: number
): string {
  const normalized = markdown.replace(/\r\n/g, '\n');
  const max = Math.max(0, maxConsecutive);

  let insideFence = false;
  let fenceMarker: '```' | '~~~' | null = null;
  let lineStartIndex = 0;

  let out = '';
  let currentRun = 0;

  for (let i = 0; i < normalized.length; i++) {
    if (i === lineStartIndex) {
      const fenceMatch = normalized.slice(i).match(/^\s*(```+|~~~+)/);
      if (fenceMatch) {
        const marker = fenceMatch[1].startsWith('```') ? '```' : '~~~';
        if (!insideFence) {
          insideFence = true;
          fenceMarker = marker;
        } else if (fenceMarker === marker) {
          insideFence = false;
          fenceMarker = null;
        }
      }
    }

    const ch = normalized[i];

    if (!insideFence && ch === '\n') {
      currentRun++;
      if (currentRun <= max) out += ch;
    } else {
      currentRun = 0;
      out += ch;
    }

    if (ch === '\n') lineStartIndex = i + 1;
  }

  return out;
}

function isTinyMceEmptyParagraph(p: HTMLParagraphElement): boolean {
  const hasMedia =
    p.querySelector(
      'img,video,audio,iframe,object,embed,svg,table,ul,ol,pre,code,hr'
    ) !== null;
  if (hasMedia) return false;

  const text = (p.textContent ?? '').replace(/\u00A0/g, ' ').trim();
  if (text.length > 0) return false;

  return Array.from(p.childNodes).every((n) => {
    if (n.nodeType === Node.TEXT_NODE) {
      return (n.textContent ?? '').replace(/\u00A0/g, ' ').trim().length === 0;
    }
    if (n.nodeType === Node.ELEMENT_NODE) {
      const el = n as Element;
      return el.tagName.toLowerCase() === 'br';
    }
    return true;
  });
}

function convertHtmlToMarkdownPreserveEmptyParagraphs(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const blocks: string[] = [];

  const pushMarkdownBlock = (blockHtml: string) => {
    const md = converter.makeMarkdown(blockHtml);
    // Avoid makeMarkdown adding its own trailing whitespace/newlines.
    blocks.push(md.replace(/\r\n/g, '\n').trimEnd());
  };

  doc.body.childNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      if (el.tagName.toLowerCase() === 'p') {
        const p = el as HTMLParagraphElement;
        if (isTinyMceEmptyParagraph(p)) {
          blocks.push('');
        } else {
          pushMarkdownBlock(p.outerHTML);
        }
        return;
      }

      pushMarkdownBlock(el.outerHTML);
      return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent ?? '').replace(/\u00A0/g, '');
      if (text.trim().length > 0) {
        // Wrap stray text into a paragraph so markdown conversion is stable.
        pushMarkdownBlock(`<p>${text}</p>`);
      }
    }
  });

  return blocks.join('\n\n');
}

function convertMarkdownToTinyMceHtml(markdown: string): string {
  const normalized = markdown.replace(/\r\n/g, '\n');

  const parts: string[] = [];
  let buffer = '';

  let lineStartIndex = 0;
  let insideFence = false;
  let fenceMarker: '```' | '~~~' | null = null;

  const flush = () => {
    if (!buffer) return;
    const html = converter.makeHtml(buffer).trim();
    if (html) parts.push(html);
    buffer = '';
  };

  for (let i = 0; i < normalized.length; ) {
    if (i === lineStartIndex) {
      const fenceMatch = normalized.slice(i).match(/^\s*(```+|~~~+)/);
      if (fenceMatch) {
        const marker = fenceMatch[1].startsWith('```') ? '```' : '~~~';
        if (!insideFence) {
          insideFence = true;
          fenceMarker = marker;
        } else if (fenceMarker === marker) {
          insideFence = false;
          fenceMarker = null;
        }
      }
    }

    // TinyMCE-style paragraphs: treat \n\n as a separator between <p> blocks.
    // For runs longer than 2 newlines, insert empty paragraphs between the two sides.
    if (!insideFence && normalized[i] === '\n' && normalized[i + 1] === '\n') {
      let j = i;
      while (j < normalized.length && normalized[j] === '\n') j++;
      const runLength = j - i;

      const hadLeftContent = buffer.length > 0 || parts.length > 0;
      const hasRightContent = j < normalized.length;

      flush();

      // Each pair (\n\n) is a paragraph boundary; extra pairs beyond the first represent
      // empty paragraphs between content blocks.
      // Example: "a\n\n\n\nb" => runLength=4 => pairs=2 => insert 1 empty <p></p>.
      const pairs = Math.floor(runLength / 2);

      // If the newline run is leading or trailing, there is no “real” boundary on one side,
      // so every pair corresponds to an empty paragraph.
      const emptyParagraphs =
        hadLeftContent && hasRightContent ? Math.max(0, pairs - 1) : pairs;
      for (let k = 0; k < emptyParagraphs; k++)
        parts.push(EMPTY_PARAGRAPH_HTML);

      // Skip the newline run; if there's a leftover single '\n' (odd runLength), keep it
      // as a soft break at the start of the next segment.
      i = j;
      if (runLength % 2 === 1 && hasRightContent) {
        buffer = '\n';
      }
      lineStartIndex = i;
      continue;
    }

    const ch = normalized[i];
    buffer += ch;
    if (ch === '\n') lineStartIndex = i + 1;
    i++;
  }

  flush();
  return parts.join('');
}

// move leading and trailing spaces from strong and em tags to the parent element
function moveSpacesFromStrongAndEmTags(html: string) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const strongs = doc.querySelectorAll('strong');
  strongs.forEach((strong) => {
    if (strong.firstChild) {
      const text = strong.firstChild.textContent;
      if (text) {
        strong.firstChild.textContent = text.trim();
        if (text.startsWith(' ')) {
          strong.parentNode?.insertBefore(document.createTextNode(' '), strong);
        }
        if (text.endsWith(' ')) {
          strong.parentNode?.insertBefore(
            document.createTextNode(' '),
            strong.nextSibling
          );
        }
      }
    }
  });
  const ems = doc.querySelectorAll('em');
  ems.forEach((em) => {
    if (em.firstChild) {
      const text = em.firstChild.textContent;
      if (text) {
        em.firstChild.textContent = text.trim();
        if (text.startsWith(' ')) {
          em.parentNode?.insertBefore(document.createTextNode(' '), em);
        }
        if (text.endsWith(' ')) {
          em.parentNode?.insertBefore(
            document.createTextNode(' '),
            em.nextSibling
          );
        }
      }
    }
  });
  return doc.body.innerHTML;
}

// replace \x3C! with < and \x3E! with >
function replaceHtmlCommentTags(html: string) {
  return html.replace(/\\x3C!/g, '<').replace(/\\x3E!/g, '>');
}

// remove html comment tags from html
function removeHtmlComments(html: string) {
  return html.replace(/(<!--.*?-->)|(<!--[\S\s]+?-->)|(<!--[\S\s]*?$)/g, '');
}

export const convertHtmlToMarkdownPoem = (
  html: string | undefined
): string | undefined => {
  if (!html) return html;

  // Keep backward compatibility with the old pipeline:
  // 1) normalize and remove HTML comments
  // 2) preserve spaces around <strong>/<em>
  let middlePoints = html;
  middlePoints = replaceHtmlCommentTags(middlePoints);
  middlePoints = removeHtmlComments(middlePoints);
  middlePoints = moveSpacesFromStrongAndEmTags(middlePoints);

  // Marker-free normalization: preserve TinyMCE empty paragraphs as real blank Markdown
  // paragraphs by converting block-by-block (instead of injecting a temporary token).
  let markdownText = convertHtmlToMarkdownPreserveEmptyParagraphs(middlePoints);

  // 4) apply the same comment cleanup on markdown output
  markdownText = replaceHtmlCommentTags(markdownText);
  markdownText = removeHtmlComments(markdownText);

  return markdownText;
};

export const convertMarkdownToHtmlPoem = (
  markdown: string | undefined
): string | undefined => {
  if (!markdown) return markdown;

  const cleaned = trimConsecutiveNewLines(markdown, 6);

  // Preserve TinyMCE semantics:
  // - `\n\n` => new paragraph
  // - additional newlines beyond that => empty paragraphs
  // - single `\n` inside a paragraph => <br />
  return convertMarkdownToTinyMceHtml(cleaned);
};
